import React from 'react';
import styled from 'styled-components';
import {Drop, DropType} from '../../../../grpc_client/grips/protos/shared/drop';
import {useDropsTranslationContext} from '../../../../pages/Drops/context/TranslationProvider';
import {DROP_PATH_TO_TYPE_MAP} from '../../../../pages/Drops/utils/constants';
import {SimpleButton} from '../../../../ui/Buttons/SimpleButton';
import {cdnImageUrl} from '../../../../utils/cdnImageUrl';
import useDropClickHandler from '../../../../utils/hooks/useDropClick';
import useDropCountDownText from '../../../../utils/hooks/useDropCountDownText';
import useDropShareUrl from '../../../../utils/hooks/useDropShareUrl';
import useDropTypeFromPath from '../../../../utils/hooks/useDropTypeFromPath';
import {useMedia} from '../../../../utils/hooks/useMedia';
import {mediaMinDesktop} from '../../../constants/customMedia';
import {fsM, fwMediumBold} from '../../../constants/typography';
import {isTimelyUnlimitedDrop} from '../../utils/helper';
import DropsPromotionTracking from '../TrackingWrapper/DropsPromotionTracking';
import {
  DetailArea,
  DropInfo,
  StyledDescription,
  StyledInfoIcon,
} from '../shared/DropInfo';
import {DropTeaserArea} from '../shared/DropTeaserArea';

const Container = styled.section`
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 10px 15px 0 rgba(0, 0, 0, 0.08),
    3px -5px 5px 0 rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  overflow: hidden;
  width: 100%;

  & + & {
    margin-top: 20px;
  }

  @media ${mediaMinDesktop} {
    flex-direction: row;
  }
  ${DetailArea} {
    div {
      ${StyledInfoIcon} {
        @media ${mediaMinDesktop} {
          display: none;
        }
      }
      ${StyledDescription} {
        @media ${mediaMinDesktop} {
          display: block;
          font-size: ${fsM}px;
          ${fwMediumBold};
          line-height: 1.5;
        }
      }
    }
  }
`;

const StyledCTAButton = styled(SimpleButton)`
  align-self: flex-start;
  width: auto;
  margin-top: 22px;

  @media ${mediaMinDesktop} {
    width: 241px;
    height: 48px;
    border-radius: 2px;
  }
`;

const StyledDropTeaserArea = styled(DropTeaserArea)`
  height: 375px;
  @media ${mediaMinDesktop} {
    width: 713px;
    height: 639px;
    background-position: top;
  }
`;

interface Props {
  drop?: Drop;
}
export const ActiveDrop: React.FC<Props> = ({drop}) => {
  const dropType = useDropTypeFromPath();
  const isLabelDrop =
    DROP_PATH_TO_TYPE_MAP[dropType] === DropType.DROP_TYPE_AY_LABEL;
  const isPremiumDrop =
    DROP_PATH_TO_TYPE_MAP[dropType] === DropType.DROP_TYPE_AY_PREMIUM;
  const {
    overview: {
      activeDrop: {shopNow: goToDetailButtonTitle},
    },
    exclusiveAtAboutYouDrop,
    nowNew,
  } = useDropsTranslationContext();
  const isDesktop = useMedia(
    {
      [mediaMinDesktop]: true,
    },
    false,
  );

  const isTimelyUnlimited = isTimelyUnlimitedDrop(drop);

  const onDropClick = useDropClickHandler(drop);

  const {countdownPillText} = useDropCountDownText(
    {
      releasedUntil: drop?.deprecatedReleasedUntil,
    },
    {
      isActiveDrop: true,
    },
  );

  const shouldShowSubline = isLabelDrop || isPremiumDrop;

  const backgroundImageUrl = isDesktop
    ? cdnImageUrl(drop?.desktopImage ?? '')
    : cdnImageUrl(drop?.mobileWebImage ?? '');
  const shareUrl = useDropShareUrl(drop?.id);
  return (
    <DropsPromotionTracking
      name={drop?.informationText || ''}
      id={`${drop?.id}`}
      url={`/d/${dropType}/${drop?.id}`}
    >
      {({ref, onClickCapture}) => (
        <Container ref={ref} onClick={onClickCapture}>
          <StyledDropTeaserArea
            backgroundImageUrl={backgroundImageUrl}
            isPillStickyTop={!isDesktop}
            headline={drop?.headline}
            subline={drop?.subline}
            onClick={onDropClick}
            usps={drop?.usps}
            {...(isLabelDrop &&
              !isTimelyUnlimited && {
                countdownText: countdownPillText,
              })}
            shareUrl={shareUrl}
          />
          <DropInfo
            headline={drop?.brandName}
            informationText={drop?.informationText}
            {...(shouldShowSubline && {
              subline: isLabelDrop ? exclusiveAtAboutYouDrop : nowNew,
            })}
            {...(isDesktop && {
              CTAButton: (
                <StyledCTAButton onClick={onDropClick}>
                  {goToDetailButtonTitle}
                </StyledCTAButton>
              ),
            })}
          />
        </Container>
      )}
    </DropsPromotionTracking>
  );
};
